import axios from 'axios';
import { push } from 'react-router-redux';
import moment from 'moment-timezone';
import * as Sentry from '@sentry/browser';
import { handleError, handleUnexpectedAuthError, captureNetworkException } from '../global/error_handler';
import { reportCdnReachabilityAfterAuth } from '../global/cdnReachabilityProbe';
import { reportAssetRescueActivationAfterAuth } from '../global/reportAssetRescueActivation';
import { fetchFeatureFlags, saveFeatureFlags, loadFeatureFlags, saveUserFlags } from '../global/featureFlags';
import clearM8LocalStorage from '../global/clearM8LocalStorage';
import {
  OTP_DELIVERY,
  OTP_METHODS_HEADER,
  SUPPORTED_OTP_METHODS,
  clearOtpDelivery,
  saveOtpDelivery,
  saveResentOtpDelivery,
} from '../global/otpDelivery';
import {
  PRESERVED_LEARNING_KEYS,
  clearTutorialSession,
} from '../components/tutorial/tutorialProgress';
import { PRESERVED_MAC_PRINTER_DRIVER_ANNOUNCEMENT_KEYS } from '../util/macPrinterDriverAnnouncement';
import { PRESERVED_AI_ON_UPLOADS_PROMPT_KEYS } from '../util/aiOnUploadsPrompt';
import { PRESERVED_SIDE_PANEL_TIP_KEYS } from '../util/sidePanelTip';
import crud from './helpers/crud';
import { resetSessionAnalytics } from './helpers/sessionAnalytics';
import { SSO_RETURN_PATH_KEY, isPrinterSignInPage } from '../util/appSignIn';
import { hideModal } from './modal';
import {
  normalizeNavPreferences,
  readStoredNavPreferences,
  readStoredNavPreferencesOrganizationId,
  storeNavPreferences,
} from '../containers/NewNav/navPreferences';
import posthog from 'posthog-js';

export const USER_AUTH = {
  LOGGING_IN: 'LOGGING_IN',
  LOGGED_IN: 'LOGGED_IN',
  LOGGED_OUT: 'LOGGED_OUT',
  LOGIN_FAILURE: 'LOGIN_FAILURE',
  LOGOUT_FAILED: 'USER_LOGOUT_FAILED',
  VALID: 'VALID',
  INVALID: 'INVALID',
  MANUAL_AI_ON_UPLOADS_CHANGED: 'MANUAL_AI_ON_UPLOADS_CHANGED',
};
// Add constants for the Resend action types
export const RESEND_OTP = {
  RESEND_OTP_START: 'RESEND_OTP_START',
  RESEND_OTP_FULFILLED: 'RESEND_OTP_FULFILLED',
  REDIRECT: 'REDIRECT',
  RESEND_OTP_FAILED: 'RESEND_OTP_FAILED',
};

export const ACCESS_CODE_AUTH = {
  BEGIN: 'BEGIN',
  SUCCESS: 'SUCCESS',
  FAILURE: 'FAILURE',
};

export const ACCESS_CODE_LOGOUT = {
  LOGOUT_SUCCESS: 'LOGOUT_SUCCESS',
};

export const VERIFY_GUEST_TOKEN = {
  BEGIN: 'VERIFY_GUEST_TOKEN_BEGIN',
  SUCCESS: 'VERIFY_GUEST_TOKEN_SUCCESS',
  FAILURE: 'VERIFY_GUEST_TOKEN_FAILURE',
};

export const CREATE_GUEST_ACCOUNT = {
  BEGIN: 'CREATE_GUEST_ACCOUNT_BEGIN',
  FAILURE: 'CREATE_GUEST_ACCOUNT_FAILURE',
};

export const USER_PROFILE = {
  CHANGE_PASSWORD_SUCCESS: 'CHANGE_PASSWORD_SUCCESS',
  CHANGE_PASSWORD_FAILED: 'CHANGE_PASSWORD_FAILED',
};

export const PATIENT_AUTH = {
  PATIENT_LOGGING_IN: 'PATIENT_LOGGING_IN',
  PATIENT_LOGGED_IN: 'PATIENT_LOGGED_IN',
  PATIENT_LOGGED_OUT: 'PATIENT_LOGGED_OUT',
  PATIENT_LOGIN_FAILURE: 'PATIENT_LOGIN_FAILURE',
  PATIENT_LOGOUT_FAILED: 'PATIENT_LOGOUT_FAILED',
  PATIENT_VALID: 'PATIENT_VALID',
  PATIENT_INVALID: 'PATIENT_INVALID',
  RESET_PATIENT_ACCESS_ATTEMPT: 'RESET_PATIENT_ACCESS_ATTEMPT',
};

export const SECURITY_QUESTION = {
  RESEND_SECURITY_START: 'RESEND_OTP_START',
  RESEND_SECURITY_FULFILLED: 'RESEND_OTP_FULFILLED',
  PATIENT_REDIRECT: 'PATIENT_REDIRECT',
  RESEND_SECURITY_FAILED: 'RESEND_OTP_FAILED',
};

export const RESET_PASSWORD = {
  BEGIN: 'RESET_PASSWORD_BEGIN',
  FAILED: 'RESET_PASSWORD_FAILED',
  SUCCESS: 'RESET_PASSWORD_SUCCESS',
};

export const FORGOT_PASSWORD = {
  BEGIN: 'FORGOT_PASSWORD_BEGIN',
  FAILED: 'FORGOT_PASSWORD_FAILED',
  SUCCESS: 'FORGOT_PASSWORD_SUCCESS',
};

export const VALIDATE_RESET_TOKEN = {
  BEGIN: 'VALIDATE_TOKEN_BEGIN',
  FAILED: 'VALIDATE_TOKEN_FAILED',
  SUCCESS: 'VALIDATE_TOKEN_SUCCESS',
};

export const LIST_ORGANIZATIONS = {
  BEGIN: 'BEGIN_CHECKING_MULTI_ORG_LOGIN',
  FAILED: 'FAILED_CHECKING_MULTI_ORG_LOGIN',
  SUCCESS: 'FINISHED_CHECKING_MULTI_ORG_LOGIN',
};

export const EXTEND_USER_SESSION = {
  BEGIN: 'BEGIN_EXTEND_USER_SESSION',
  FAILED: 'FAILED_EXTEND_USER_SESSION',
  SUCCESS: 'FINISHED_EXTEND_USER_SESSION',
};

// Canary/stable redirect plumbing. The HttpOnly `_m8_canary_routing` cookie is
// not readable from JS, so the SPA relies on (a) the `routing_deployment` field
// in auth responses and (b) the server-rendered `m8-deployment-type` meta tag
// to detect when the page was served by the wrong deployment track for the
// signed-in user's org.
const ROUTING_REDIRECT_KEY = '_m8_routing_redirects';
const ROUTING_VERIFIED_KEY = '_m8_routing_verified';
const POST_AUTH_SENTINEL_KEY = 'm8_post_auth_transition';
const POST_AUTH_DISPLAY_NAME_KEY = 'm8_post_auth_display_name';
const MAX_ROUTING_REDIRECTS = 2;

// Set to true the instant `redirectIfDeploymentMismatch` calls
// `window.location.assign(...)`. Callers in async pipelines (notably
// `validateAuth`) check this after their `await Promise.all(...)` so they
// don't continue past the redirect and dispatch USER_AUTH.VALID — which on
// the source page would flip `authReady` true, fade the SplashScreen, and
// clear the sentinel before the destination loads.
let _routingRedirectInProgress = false;

const isRoutingRedirectInProgress = () => _routingRedirectInProgress;

// Test-only hook. In production the flag's lifetime is the JS context up to
// the `window.location.assign(...)` unload — there is no path that needs
// to clear it. Jest/Bun keeps the module alive across tests, so tests need
// to reset it explicitly between cases that span a redirect.
export const __testHooks = {
  resetRoutingRedirectInProgress: () => { _routingRedirectInProgress = false; },
};

const DEFAULT_POST_AUTH_DESTINATION = '/app';
const APP_PATH = /^\/app(?:[/?#]|$)/;
const MICROSOFT_SIGN_IN_PAGE = /^\/app\/login_microsoft\/?$/;

/**
 * The same-origin path under `/app` that `candidate` names, or null. Anything
 * else (absolute and protocol-relative URLs, backslash tricks, dot segments
 * leaving `/app`) is refused, so no input can become an open redirect.
 *
 * @param {*} candidate a path with optional query and hash
 * @returns {string|null}
 */
const sameOriginAppPath = (candidate) => {
  if (typeof candidate !== 'string' || !APP_PATH.test(candidate) || candidate.includes('\\')) {
    return null;
  }
  try {
    const target = new URL(candidate, window.location.origin);
    if (target.origin !== window.location.origin || !APP_PATH.test(target.pathname)) return null;
    return `${target.pathname}${target.search}${target.hash}`;
  } catch {
    return null;
  }
};

/**
 * Where a cross-track hard navigation should land. Navigating to `/app`
 * unconditionally sent a first, cookie-less sign-in from the printer sign-in
 * page (`/app/printer_sign_in`, opened by Gibraltar and the Medsender iOS app)
 * to the dashboard, and the client never received its sign-in link. In order:
 *
 * 1. The login page's `?redirect=<path>` (redux-auth-wrapper).
 * 2. The printer sign-in page itself, when the browser is already on it. The
 *    login's wrapper moves there as soon as LOGGING_IN sets `isLoading`, before
 *    `/auth/sign_in` answers (password and verification-code steps alike), and
 *    a signed-in sheet loads it directly (validateAuth).
 * 3. On the Microsoft sign-in callback page, the path the Microsoft sign-in
 *    started from (LoginMicrosoft would have gone there).
 *
 * @returns {string} an app-relative path
 */
const postAuthDestination = () => {
  try {
    const { pathname, search, hash } = window.location;

    const redirect = sameOriginAppPath(new URLSearchParams(search).get('redirect'));
    if (redirect) return redirect;

    if (isPrinterSignInPage(pathname)) {
      return sameOriginAppPath(`${pathname}${search}${hash}`) || DEFAULT_POST_AUTH_DESTINATION;
    }

    if (MICROSOFT_SIGN_IN_PAGE.test(pathname)) {
      return sameOriginAppPath(sessionStorage.getItem(SSO_RETURN_PATH_KEY)) || DEFAULT_POST_AUTH_DESTINATION;
    }
  } catch { /* fall through to the default */ }
  return DEFAULT_POST_AUTH_DESTINATION;
};

/**
 * If the auth response indicates a different deployment than what the current
 * page was served from, prefetch the destination, set a splash sentinel, and
 * hard-navigate. The gateway routes the navigation to the correct track based
 * on the `_m8_canary_routing` cookie that the backend just set.
 *
 * Skipped in development (no canary in dev) and when the meta tag is absent or
 * matches the response. Includes a sessionStorage circuit breaker capping
 * mismatched redirects at MAX_ROUTING_REDIRECTS to avoid infinite loops if the
 * gateway is misconfigured or the CDN is serving stale HTML.
 *
 * @param {Object} response axios response from an auth endpoint
 * @returns {boolean} true if a redirect was triggered; callers should bail out
 */
const redirectIfDeploymentMismatch = (response) => {
  if (process.env.NODE_ENV === 'development') return false;

  const desiredDeployment = response?.data?.routing_deployment;
  const meta = typeof document !== 'undefined'
    ? document.querySelector('meta[name="m8-deployment-type"]')
    : null;
  const currentDeployment = meta?.getAttribute('content') || 'stable';

  // No hint, or already on the right deployment — clear the breaker counter
  // and let the caller continue normally.
  if (!desiredDeployment || desiredDeployment === currentDeployment) {
    try {
      sessionStorage.removeItem(ROUTING_REDIRECT_KEY);
      // Mark this tab's track as verified against the server. Lets `validateAuth`
      // skip the /api/v1/users routing check on subsequent loads in the same
      // tab when localStorage hydration is also satisfied. Only set when we
      // got a real hint back — absence of `desiredDeployment` means the caller
      // didn't carry routing info (e.g. /auth/sign_in without the field), so
      // we can't claim verification.
      if (desiredDeployment) sessionStorage.setItem(ROUTING_VERIFIED_KEY, currentDeployment);
    } catch (_) { /* no-op */ }
    return false;
  }

  // Circuit breaker: bail out if we've already hopped MAX_ROUTING_REDIRECTS
  // times this tab session. Leave the counter at the cap on purpose — clearing
  // it would let the breaker re-arm and the same 2-hop cycle could restart on
  // the next auth call within the same tab.
  let redirectCount = 0;
  try {
    redirectCount = parseInt(sessionStorage.getItem(ROUTING_REDIRECT_KEY) || '0', 10);
  } catch (_) { /* sessionStorage may be unavailable (private mode, SSR, etc.) */ }
  if (redirectCount >= MAX_ROUTING_REDIRECTS) {
    // Leading indicator of config drift in production (e.g. DEPLOYMENT_TYPE
    // unset on canary, or PROTECTED_ORG_IDS mismatch between web/web-canary).
    // A spike in this Sentry event = the redirect mechanism is looping and
    // users are stuck on the wrong track. Reported at `warning` so it shows
    // up in issue triage without paging.
    try {
      Sentry.captureMessage('canary-routing circuit breaker tripped', {
        level: 'warning',
        extra: {
          currentDeployment,
          desiredDeployment,
          hopCount: redirectCount,
        },
      });
    } catch (_) { /* Sentry may be unavailable (e.g. ad blocker); never throw here */ }
    return false;
  }

  try {
    sessionStorage.setItem(ROUTING_REDIRECT_KEY, String(redirectCount + 1));
    sessionStorage.setItem(POST_AUTH_SENTINEL_KEY, '1');
    const displayName = response?.data?.name;
    if (displayName) sessionStorage.setItem(POST_AUTH_DISPLAY_NAME_KEY, displayName);
  } catch (_) { /* no-op */ }

  // Clear any stale m8_* localStorage from a previous session BEFORE we hard-
  // navigate. Otherwise the destination's validateAuth() can see a complete
  // localStorage (e.g. m8_uid, m8_access_received_inbox, m8_document_categories
  // all present from a prior session) and short-circuit the /api/v1/users
  // refresh, leaving redux hydrated from stale data after a cross-track hop.
  //
  // Unlike resetAuth(), this path does NOT preserve Chrome-extension tokens:
  // a cross-track redirect can imply an identity change (different user/org),
  // so keeping the source identity's `m8_chrome_ext_*` would leak stale tokens
  // into the destination session. The destination's validateAuth() refetches
  // /api/v1/users, whose response carries fresh tokens that
  // populateLocalStorageFromUserData() writes back. Email is preserved so the
  // login form can prefill.
  clearM8LocalStorage({ preserveEmail: true });

  // Read before anything navigates (the login's `?redirect=`, the current page or the SSO return path).
  const destination = postAuthDestination();

  // Best-effort pre-warm so the navigation hits HTTP cache and the SPA boots
  // faster on the destination. Failures are silent.
  try {
    fetch(destination, { credentials: 'include', cache: 'reload', mode: 'same-origin' }).catch(() => {});
    const link = document.createElement('link');
    link.rel = 'prefetch';
    link.href = destination;
    document.head.appendChild(link);
  } catch (_) { /* no-op */ }

  // Flip the module-level flag BEFORE the navigation so any async pipeline
  // (e.g. validateAuth's `await Promise.all(...)`) that resolves between this
  // call and the browser unloading the source page can see the redirect is
  // in progress and bail out before dispatching USER_AUTH.VALID.
  _routingRedirectInProgress = true;
  window.location.assign(destination);
  return true;
};

/**
 * Remove auth credentials from local storage.
 * Uses clearM8LocalStorage for consistency and completeness.
 */
const resetAuth = () => {
  clearM8LocalStorage({
    preserveChromeExtension: true,
    preserveEmail: true,
    // Tutorial progress is per-user INSIDE the value, so it survives a sign-out
    // (see tutorialProgress.PRESERVED_LEARNING_KEYS). This is the only caller that
    // asks for it; the cross-track clear above deliberately does not, because a
    // cross-track hop can imply an identity change. The Mac Printer Driver
    // announcement's "don't show this again" rides along for the same reason, and so
    // does the side panel tip's per-user seen list (ENG-3676).
    preserveKeys: new Set([
      ...PRESERVED_LEARNING_KEYS,
      ...PRESERVED_MAC_PRINTER_DRIVER_ANNOUNCEMENT_KEYS,
      ...PRESERVED_AI_ON_UPLOADS_PROMPT_KEYS,
      ...PRESERVED_SIDE_PANEL_TIP_KEYS,
    ]),
  });
  // The sessionStorage tutorial markers, by contrast, must NOT survive.
  //
  // They are per-tab "a tour was running / just finished" notes with no user
  // identity in them, and nothing cleared them at any auth boundary — `clearM8*`
  // only touches localStorage. LearnMedsender unmounts on logout and remounts for
  // the next account with a fresh per-mount latch, so the next person signing in on
  // this tab was auto-launched into the PREVIOUS user's interrupted tutorial, got
  // navigated by it, and had `tutorial_resumed` attributed to them.
  clearTutorialSession();
};

const resetSentryContext = () => {
  Sentry.setUser(null);
};

const parseArrayFromStorage = (key) => {
  const rawValue = localStorage.getItem(key);

  if (rawValue === null) return [];

  try {
    const parsedValue = JSON.parse(rawValue);
    return Array.isArray(parsedValue) ? parsedValue : [];
  } catch {
    return [];
  }
};

const hasSerializedArrayInStorage = (key) => {
  const rawValue = localStorage.getItem(key);

  if (rawValue === null) return false;

  try {
    return Array.isArray(JSON.parse(rawValue));
  } catch {
    return false;
  }
};

const serializeStorageValue = (value, fallback = '') => {
  if (value === null || value === undefined) return fallback;
  return typeof value === 'string' ? value : JSON.stringify(value);
};

const normalizeDocumentCategories = (value) => {
  if (Array.isArray(value)) return value;

  if (typeof value === 'string') {
    try {
      const parsedValue = JSON.parse(value);
      return Array.isArray(parsedValue) ? parsedValue : [];
    } catch {
      return [];
    }
  }

  return [];
};


/**
 * This fxn runs after authentication and if the current org is not set,
 * it shows the modal for setting the currently signed in organization
*/
const showMultiOrganizationLoginModal = async (dispatch) => {
  dispatch({ type: LIST_ORGANIZATIONS.BEGIN });
  try {
    const response = await axios({
      method: 'GET',
      url: '/api/v1/users/multiple_organization_login_enabled',
      headers: {
        'X-CSRF-Token': document.getElementsByName('csrf-token')[0].content,
        timezone: moment.tz.guess() || 'Eastern Time (US & Canada)',
      },
    });
    dispatch({
      type: LIST_ORGANIZATIONS.SUCCESS,
      payload: response,
    });
    const modalType = {
      type: 'SELECT_ORGANIZATION',
      data: {
        ...response.data.user,
        updateDefaultOrgCookie: true,
      },
    };
    const { data: { show_multiple_organization_login_modal: showOrgModal } } = modalType;

    // Show multi-org login modal
    if (showOrgModal) {
      dispatch({
        type: 'SHOW_MODAL',
        modalType,
      });
    }
  } catch (error) {
    dispatch({
      type: LIST_ORGANIZATIONS.FAILED,
      payload: error.response,
    });

    dispatch({
      type: 'SHOW_ERROR_DISPLAY',
      payload: error.response,
    });
  }
};


/**
 * Populates localStorage with user profile and permission data from an
 * /api/v1/users response. Used by validateAuth() to repopulate missing
 * localStorage values. getLoginInfo() should adopt this helper in a follow-up.
 *
 * Writes auth tokens (m8_access, m8_client, m8_chrome_ext_*) when present in
 * the response so post-redirect cross-track hydration recovers the destination
 * identity's fresh tokens. Api::V1::UsersController#show always includes them,
 * but the writes are guarded so callers that pass a token-less payload (e.g.
 * future profile-only consumers) don't clobber existing values with undefined.
 * Feature flags are still handled by their own pipeline.
 */
const populateLocalStorageFromUserData = (data) => {
  if (data.access_token) localStorage.setItem('m8_access', data.access_token);
  if (data.client) localStorage.setItem('m8_client', data.client);
  if (data.chrome_access_token) localStorage.setItem('m8_chrome_ext_access', data.chrome_access_token);
  if (data.chrome_client) localStorage.setItem('m8_chrome_ext_client', data.chrome_client);
  // Match existing login flows (see getLoginInfo and the OTP branch): the
  // expiry sentinel is set to a single space whenever new chrome-ext tokens
  // are written, so clients can detect "tokens present, expiry unknown".
  if (data.chrome_access_token || data.chrome_client) localStorage.setItem('m8_chrome_ext_expiry', ' ');

  localStorage.setItem('m8_uid', data.email);
  localStorage.setItem('m8_name', data.name);
  localStorage.setItem('m8_org', data.organization_name);
  localStorage.setItem('is_admin', data['is_user_admin?']);
  localStorage.setItem('m8_emr', data.emr || '');
  if (data.job_title) localStorage.setItem('m8_job_title', data.job_title);
  localStorage.setItem('m8_auto_upload_emr', data.auto_upload_emr);
  localStorage.setItem('m8_document_categories', serializeStorageValue(normalizeDocumentCategories(data.document_categories), '[]'));
  localStorage.setItem('m8_plan_type', data.plan_type);
  localStorage.setItem('m8_billing_status', JSON.stringify(data.billing_status));
  localStorage.setItem('m8_has_completed_walkthrough', data.has_completed_walkthrough);
  localStorage.setItem('user_can_send_fax_records_only', JSON.stringify(data.user_can_send_fax_records_only));
  localStorage.setItem('trial_user', data.trial_user);
  localStorage.setItem('departments', JSON.stringify(data.organization_departments || []));
  localStorage.setItem('voice_inbox_departments', JSON.stringify(data.voice_inbox_departments || []));
  localStorage.setItem('m8_referral_management', data.referral_management || false);
  localStorage.setItem('m8_manual_ai_on_uploads', data.manual_ai_on_uploads || false);
  localStorage.setItem('m8_view_referral_analytics', data.view_referral_analytics || false);
  localStorage.setItem('referral_sms_template', serializeStorageValue(data.referral_sms_template, '[]'));
  localStorage.setItem('referral_call_templates', serializeStorageValue(data.referral_call_templates, '[]'));
  localStorage.setItem('referral_reply_templates', serializeStorageValue(data.referral_reply_templates, '[]'));
  localStorage.setItem('m8_access_received_inbox', data.access_received_inbox || false);
  localStorage.setItem('m8_access_sent_inbox', data.access_sent_inbox || false);
  localStorage.setItem('m8_access_voice_inbox', data.access_voice_inbox || false);
  localStorage.setItem('m8_sent_inbox_own_records_only', data.sent_inbox_own_records_only || false);
  localStorage.setItem('m8_patient_recapture', data.patient_recapture || false);
  localStorage.setItem('m8_patient_engagement', data.patient_engagement || false);
  storeNavPreferences(data.nav_preferences, data.nav_preferences_organization_id ?? null);
  localStorage.setItem('m8_impersonating', data.impersonating || false);
  localStorage.setItem('m8_impersonation_expires_in', data.impersonating ? data.impersonation_expires_in || '' : '');
  const defaultFaxNumber = data.user_fax_number !== null ? data.user_fax_number : '';
  localStorage.setItem('default_fax_number', defaultFaxNumber);
  localStorage.setItem('associated_fax_numbers', JSON.stringify(data.associated_fax_numbers || []));
  localStorage.setItem('login_provider', data.provider);
};

/**
 * On page load, we want to make a quick determination on whether to render
 * the authenticated or unauthenticated pages to the user. Without doing an
 * additional AJAX request, we see if the server has sent back a string with
 * "true" indicating a valid user auth session, or "false"
 *
 * If success, let user through, otherwise redirect to login/
 * @return {ActionDispatch}
 */
const validateAuth = () => async (dispatch) => {
  try {
    // Server sends back a small string if user is authenticated according
    // to their session cookie
    //
    // This string is rendered in app/views/pages/index.html.erb
    const authValid = document.getElementById('_m8.a').getAttribute('m-req');
    if (authValid === 'false') {
      return dispatch({
        type: USER_AUTH.INVALID,
      });
    }

    // If critical localStorage values are missing (e.g. EMA popup windows where
    // the Microsoft SSO flow authenticates the session before the client-side
    // login callback has populated localStorage), fetch fresh user data from
    // the server to repopulate them. See SUP-104.
    const needsLocalStorageRefresh = (
      !localStorage.getItem('m8_uid')
      || !localStorage.getItem('m8_access_received_inbox')
      || !localStorage.getItem('m8_access_voice_inbox')
      || !localStorage.getItem('m8_sent_inbox_own_records_only')
      || !localStorage.getItem('m8_patient_recapture')
      || !localStorage.getItem('m8_patient_engagement')
      || !hasSerializedArrayInStorage('m8_document_categories')
      || !hasSerializedArrayInStorage('voice_inbox_departments')
    );

    // Independent routing-state check: even when localStorage is fully
    // hydrated, if this tab has not yet verified its deployment track against
    // the server (verified-track marker absent, or stale relative to the meta
    // tag), fetch /api/v1/users so the response carries `routing_deployment`
    // and refreshes the `_m8_canary_routing` cookie. Without this, a tab with
    // complete legacy localStorage and a missing/stale routing cookie would
    // never trigger the redirect — defeating the stale-cookie recovery case.
    // Skipped in development (same as redirectIfDeploymentMismatch).
    let currentDeployment = 'stable';
    try {
      const meta = document.querySelector('meta[name="m8-deployment-type"]');
      if (meta) currentDeployment = meta.getAttribute('content') || 'stable';
    } catch (_) { /* no-op */ }
    let verifiedTrack = null;
    try { verifiedTrack = sessionStorage.getItem(ROUTING_VERIFIED_KEY); }
    catch (_) { /* sessionStorage may be unavailable */ }
    const needsRoutingCheck = process.env.NODE_ENV !== 'development'
      && verifiedTrack !== currentDeployment;

    const needsUserRefresh = needsLocalStorageRefresh || needsRoutingCheck;
    let userRefreshPromise = Promise.resolve(null);
    let refreshedRoutingOrgId = null;

    if (needsUserRefresh) {
      const csrfMeta = document.getElementsByName('csrf-token')[0];
      if (csrfMeta) {
        userRefreshPromise = axios({
          method: 'GET',
          url: '/api/v1/users',
          headers: {
            'X-CSRF-Token': csrfMeta.content,
          },
        }).then((response) => {
          // Canary routing: if the user belongs to a different deployment than
          // the page was served from (e.g. SUP-104 SSO popup landing on stable
          // when their org is canary), hard-navigate so the next page load
          // ships the correct JS bundle.
          if (redirectIfDeploymentMismatch(response)) return;
          refreshedRoutingOrgId = response.data?.routing_org_id;
          if (response.data && response.data.email) {
            populateLocalStorageFromUserData(response.data);
          }
        }).catch((fetchError) => {
          console.error('Failed to refresh user data from server:', fetchError);
        });
      } else {
        console.warn('CSRF token missing — skipping user data refresh');
      }
    }

    // Fetch feature flags in parallel with the user data refresh (if needed)
    const [featureFlagsResponse] = await Promise.all([
      fetchFeatureFlags(),
      userRefreshPromise,
    ]);

    // If the user-refresh callback triggered a cross-track redirect, bail out
    // here before dispatching USER_AUTH.VALID. Continuing would advance the
    // source page's redux store to authReady=true (with localStorage already
    // cleared by resetAuth()), which flips the SplashScreen into its fade-out
    // path and clears the post-auth sentinel — defeating the splash UX on the
    // destination page. The browser will finish unloading shortly.
    if (isRoutingRedirectInProgress()) return undefined;

    Sentry.setUser({
      email: localStorage.getItem('m8_uid'),
    });
    reportCdnReachabilityAfterAuth(refreshedRoutingOrgId);
    reportAssetRescueActivationAfterAuth(refreshedRoutingOrgId);

    const featureFlags = featureFlagsResponse.data.flags;
    const hasEmrIntegrationCredential = featureFlagsResponse.data.has_emr_integration_credential || false;

    const trialUser = JSON.parse(localStorage.getItem('trial_user'));
    const departments = parseArrayFromStorage('departments');
    const voiceInboxDepartments = parseArrayFromStorage('voice_inbox_departments');
    const defaultFaxNumber = localStorage.getItem('default_fax_number');
    const associatedFaxNumbers = parseArrayFromStorage('associated_fax_numbers');
    const billing = JSON.parse(localStorage.getItem('m8_billing_status'));
    const hasCompletedWalkthrough = JSON.parse(localStorage.getItem('m8_has_completed_walkthrough'));
    const userCanSendFaxRecordsOnly = JSON.parse(localStorage.getItem('user_can_send_fax_records_only'));
    const autoUploadEmr = localStorage.getItem('m8_auto_upload_emr');
    const documentCategories = parseArrayFromStorage('m8_document_categories');
    const planType = localStorage.getItem('m8_plan_type');
    const provider = localStorage.getItem('login_provider');

    saveFeatureFlags(featureFlags, featureFlagsResponse.data.departments);
    await showMultiOrganizationLoginModal(dispatch);

    // TODO: Check to see if trial has expired and display banner

    return dispatch({
      type: USER_AUTH.VALID,
      payload: {
        uid: localStorage.getItem('m8_uid'),
        name: localStorage.getItem('m8_name'),
        isAdmin: JSON.parse(localStorage.getItem('is_admin')) || false,
        organization: localStorage.getItem('m8_org'),
        impersonating: localStorage.getItem('m8_impersonating') === 'true',
        impersonationExpiresIn: parseInt(localStorage.getItem('m8_impersonation_expires_in'), 10) || null,
        autoUploadEmr,
        documentCategories,
        trialUser,
        departments,
        voiceInboxDepartments,
        featureFlags,
        billing,
        emr: localStorage.getItem('m8_emr'),
        referralManagement: localStorage.getItem('m8_referral_management') === 'true',
        manualAiOnUploads: localStorage.getItem('m8_manual_ai_on_uploads') === 'true',
        viewReferralAnalytics: localStorage.getItem('m8_view_referral_analytics') === 'true',
        accessVoiceInbox: localStorage.getItem('m8_access_voice_inbox') === 'true',
        patientRecapture: localStorage.getItem('m8_patient_recapture') === 'true',
        patientEngagement: localStorage.getItem('m8_patient_engagement') === 'true',
        navPreferences: readStoredNavPreferences(),
        navPreferencesOrganizationId: readStoredNavPreferencesOrganizationId(),
        jobTitle: localStorage.getItem('m8_job_title'),
        defaultFaxNumber,
        associatedFaxNumbers,
        hasCompletedWalkthrough,
        userCanSendFaxRecordsOnly,
        planType,
        provider,
        hasEmrIntegrationCredential,
      },
    });
  } catch (error) {
    resetSentryContext();
    return dispatch({
      type: USER_AUTH.INVALID,
    });
  }
};

/**
 * Reset the user session
 */
const extendUserSession = () => (
  crud({
    dispatch: {
      begin: EXTEND_USER_SESSION.BEGIN,
      fail: EXTEND_USER_SESSION.FAILED,
      end: EXTEND_USER_SESSION.SUCCESS,
    },
    method: 'POST',
    url: '/api/v1/session_timeout',
  })
);

/**
 * Handle user logout
 * @return {ActionDispatch}
 */
const logout = () => async (dispatch) => {
  try {
    dispatch(hideModal());
    const response = await axios({
      method: 'DELETE',
      url: '/auth/sign_out',
      headers: {
        'X-CSRF-Token': document.getElementsByName('csrf-token')[0].content,
      },
    });
    const newCSRFToken = response.headers['x-csrf-token'];
    document.getElementsByName('csrf-token')[0].setAttribute('content', newCSRFToken);
    dispatch({
      type: USER_AUTH.LOGGED_OUT,
    });
    // Redirect user to login
    dispatch(push('/login'));
    resetAuth();
    resetSentryContext();
    if (process.env.NODE_ENV !== 'development' && posthog) {
      posthog.reset();
    }
    resetSessionAnalytics();
  } catch (error) {
    dispatch({
      type: USER_AUTH.LOGOUT_FAILED,
    });
    resetAuth();
    resetSentryContext();
  }
};

const getLoginInfo = (options = {}) => async (dispatch) => {
  const { rejectOnError = false } = options;
  dispatch({
    type: 'BEGIN_GET_LOGIN_INFO',
  });

  try {
    await axios({
      method: 'GET',
      url: '/api/v1/users',
      headers: {
        'X-CSRF-Token': document.getElementsByName('csrf-token')[0].content,
      },
    }).then(async (response) => {
      // Canary routing: if the user's org maps to a different deployment than
      // the page was served from, hard-navigate before doing any redux work.
      // Covers Microsoft SSO callback landing on the wrong track.
      if (redirectIfDeploymentMismatch(response)) return;
      const {
        data: {
          name,
          email,
          trial_user: trialUser,
          organization_departments: departments,
          voice_inbox_departments: voiceInboxDepartments,
          access_token: accessToken,
          client,
          chrome_client: chromeClient,
          chrome_access_token: chromeAccessToken,
          has_completed_walkthrough: hasCompletedWalkthrough,
          user_can_send_fax_records_only: userCanSendFaxRecordsOnly,
          provider,
          plan_type: planType,
          allowed_user_domains: allowedUserDomains,
          organization_name: organization,
          auto_upload_emr: autoUploadEmr,
          document_categories: documentCategories,
          referral_management: referralManagement,
          manual_ai_on_uploads: manualAiOnUploads,
          view_referral_analytics: viewReferralAnalytics,
          organization_details: organizationDetails,
          available_emrs: availableEmrs,
          customer_payment_id: customerPaymentId,
          intercom_hmac: intercomHmac,
          current_signed_in_organization_id: currentOrganizationId,
          impersonating,
          impersonation_expires_in: impersonationExpiresIn,
          tracking,
          referral_sms_template: referralSmsTemplate,
          referral_call_templates: referralCallTemplates,
          referral_reply_templates: referralReplyTemplates,
        },
      } = response;
      const normalizedDocumentCategories = normalizeDocumentCategories(documentCategories);

      localStorage.setItem('m8_has_completed_walkthrough', hasCompletedWalkthrough);
      localStorage.setItem('user_can_send_fax_records_only', JSON.stringify(userCanSendFaxRecordsOnly));
      Sentry.setUser({
        email,
      });
      reportCdnReachabilityAfterAuth(response?.data?.routing_org_id);
      reportAssetRescueActivationAfterAuth(response?.data?.routing_org_id);

      const featureResponse = await fetchFeatureFlags().catch((error) => {
        return handleUnexpectedAuthError(error, dispatch, USER_AUTH.LOGIN_FAILURE);
      });

      const { data: { flags, user_flags: userFlags, departments: allDepartments, has_emr_integration_credential: hasEmrIntegrationCredential } } = featureResponse;

      saveFeatureFlags(flags, allDepartments);
      saveUserFlags(userFlags);
      const newCSRFToken = response.headers['x-csrf-token'];
      document.getElementsByName('csrf-token')[0].setAttribute('content', newCSRFToken);

      localStorage.setItem('m8_billing_status', JSON.stringify(response.data.billing_status));

      // Sets the new access token, expiry and client token onto the localStorage.
      // Sets value to single space since there is no expiry in the response.
      localStorage.setItem('m8_access', accessToken);
      localStorage.setItem('m8_client', client);
      localStorage.setItem('m8_expiry', ' ');
      localStorage.setItem('m8_chrome_ext_access', chromeAccessToken);
      localStorage.setItem('m8_chrome_ext_client', chromeClient);
      localStorage.setItem('m8_chrome_ext_expiry', ' ');
      if (response.data.job_title) localStorage.setItem('m8_job_title', response.data.job_title);
      localStorage.setItem('m8_emr', response.data.emr || '');
      localStorage.setItem('m8_nymbl_tenant_id', response.data.available_emrs.find((emr) => emr?.tenant_id)?.tenant_id);
      localStorage.setItem('m8_referral_management', response.data.referral_management || false);
      localStorage.setItem('m8_manual_ai_on_uploads', response.data.manual_ai_on_uploads || false);
      localStorage.setItem('m8_view_referral_analytics', response.data.view_referral_analytics || false);
      // Set inbox access flags BEFORE dispatch to avoid race condition where
      // components read localStorage before values are set
      localStorage.setItem('m8_access_received_inbox', response.data.access_received_inbox || false);
      localStorage.setItem('m8_access_sent_inbox', response.data.access_sent_inbox || false);
      localStorage.setItem('m8_access_voice_inbox', response.data.access_voice_inbox || false);
      localStorage.setItem('m8_sent_inbox_own_records_only', response.data.sent_inbox_own_records_only || false);
      localStorage.setItem('m8_patient_recapture', response.data.patient_recapture || false);
      localStorage.setItem('m8_patient_engagement', response.data.patient_engagement || false);
      storeNavPreferences(response.data.nav_preferences, response.data.nav_preferences_organization_id ?? null);

      dispatch({
        type: USER_AUTH.LOGGED_IN,
        payload: {
          name,
          uid: email,
          trialUser,
          departments,
          voiceInboxDepartments,
          flags,
          allDepartments,
          organization_name: organization,
          autoUploadEmr,
          documentCategories: normalizedDocumentCategories,
          billing: response.data.billing_status,
          emr: response.data.emr || '',
          referralManagement: response.data.referral_management,
          manualAiOnUploads: response.data.manual_ai_on_uploads,
          viewReferralAnalytics: response.data.view_referral_analytics,
          accessVoiceInbox: response.data.access_voice_inbox || false,
          patientRecapture: response.data.patient_recapture || false,
          patientEngagement: response.data.patient_engagement || false,
          jobTitle: response.data.job_title || '',
          defaultFaxNumber: response.data.user_fax_number,
          associatedFaxNumbers: response.data.associated_fax_numbers,
          isAdmin: response.data['is_user_admin?'],
          navPreferences: normalizeNavPreferences(response.data.nav_preferences),
          navPreferencesOrganizationId: response.data.nav_preferences_organization_id ?? null,
          hasCompletedWalkthrough,
          provider,
          planType,
          organization,
          organizationDetails,
          intercomHmac,
          currentOrganizationId,
          impersonating,
          impersonationExpiresIn,
          hasEmrIntegrationCredential,
        },
      });
      localStorage.setItem('m8_uid', email);
      localStorage.setItem('m8_name', name);
      localStorage.setItem('m8_org', organization);
      localStorage.setItem('m8_impersonating', impersonating || false);
      localStorage.setItem('m8_impersonation_expires_in', impersonating ? impersonationExpiresIn || '' : '');
      localStorage.setItem('m8_auto_upload_emr', autoUploadEmr);
      localStorage.setItem('m8_document_categories', serializeStorageValue(normalizedDocumentCategories, '[]'));
      localStorage.setItem('is_admin', response.data['is_user_admin?']);
      localStorage.setItem('trial_user', trialUser);
      localStorage.setItem('departments', JSON.stringify(departments) || []);
      localStorage.setItem('voice_inbox_departments', JSON.stringify(voiceInboxDepartments || []));
      localStorage.setItem('allDepartments', JSON.stringify(allDepartments) || []);
      localStorage.setItem('allowed_user_domains', allowedUserDomains);
      localStorage.setItem('m8_plan_type', planType);
      // Save fax numbers data
      const { user_fax_number: userFaxNumber, associated_fax_numbers: associatedFaxNumbers } = response.data;
      const defaultFaxNumber = response.data.user_fax_number !== null ? userFaxNumber : '';
      localStorage.setItem('default_fax_number', defaultFaxNumber);
      localStorage.setItem('associated_fax_numbers', JSON.stringify(associatedFaxNumbers));
      localStorage.setItem('login_provider', provider);
      localStorage.setItem('customer_payment_id', customerPaymentId);
      localStorage.setItem('tracking', tracking);
      localStorage.setItem('referral_sms_template', serializeStorageValue(referralSmsTemplate, '[]'));
      localStorage.setItem('referral_call_templates', serializeStorageValue(referralCallTemplates, '[]'));
      localStorage.setItem('referral_reply_templates', serializeStorageValue(referralReplyTemplates, '[]'));
    }).catch((error) => {
      // When rejectOnError is true (e.g., impersonation refresh), re-throw
      // immediately without dispatching LOGIN_FAILURE. Dispatching first would
      // wipe Redux auth state (including impersonating flag), causing the
      // impersonation banner to vanish on transient API errors.
      if (rejectOnError) {
        throw error;
      }

      const forceRefresh = error.response.data.force || false;
      const alreadySignedIn = error.response && error.response.status === 409;
      const errorMessage = handleError(error);

      dispatch({
        type: USER_AUTH.LOGIN_FAILURE,
        payload: {
          error: errorMessage,
        },
      });
      // If the API asks us to force refresh the page in the event of a CSRF
      // or cookie mismatch, wait 2 seconds before initiating
      if (forceRefresh || alreadySignedIn) {
        setTimeout(() => {
          window.location.reload();
        }, 200);
      }
    });
  } catch (error) {
    if (rejectOnError) {
      throw error;
    }

    handleUnexpectedAuthError(error, dispatch, USER_AUTH.LOGIN_FAILURE);
  }
};

/**
 * Authenticate user with backend. This function is used for both regular auth
 * and 2FA. The rememberEmail param is not used for 2FA.
 * @param  {Object} data
 * @param  {Boolean} rememberEmail whether to remember the email or not
 * @return {Void}
 */
const login = (data, rememberEmail) => async (dispatch) => {
  dispatch({
    type: USER_AUTH.LOGGING_IN,
  });

  // This param is only used for email/password authentication and will
  // be undefined during 2FA. That's why we're checking the actual truthy
  // values before handling a response.
  if (rememberEmail === false) {
    localStorage.removeItem('m8_email');
  } else if (rememberEmail === true) {
    localStorage.setItem('m8_email', data.email);
  }

  try {
    axios({
      method: 'POST',
      url: '/auth/sign_in',
      data: {
        user: data,
        timezone: moment.tz.guess() || 'Eastern Time (US & Canada)',
        os: window.navigator.platform,
      },
      headers: {
        'X-CSRF-Token': document.getElementsByName('csrf-token')[0].content,
        // Lets the server skip emailing a code to a user who signs in with an
        // authenticator app. Clients that omit it keep receiving email codes.
        [OTP_METHODS_HEADER]: SUPPORTED_OTP_METHODS,
      },
    }).then(async (response) => {
      // Response status 201 means login is successful
      // 200 means 2FA is needed, so redirect to login page to show the OTP form

      const {
        data: {
          name,
          email,
          trial_user,
          organization_departments: departments,
          voice_inbox_departments: voiceInboxDepartments,
          access_token,
          client,
          chrome_access_token,
          chrome_client,
          organization_name: organizationName,
          auto_upload_emr: autoUploadEmr,
          document_categories: documentCategories,
          referral_management: referralManagement,
          manual_ai_on_uploads: manualAiOnUploads,
          view_referral_analytics: viewReferralAnalytics,
          allowedUserDomains,
          has_completed_walkthrough: hasCompletedWalkthrough,
          user_can_send_fax_records_only: userCanSendFaxRecordsOnly,
          billing_status: billingStatus,
          provider,
          organization_details: organizationDetails,
          available_emrs: availableEmrs,
          customer_payment_id: customerPaymentId,
          intercom_hmac: intercomHmac,
          tracking,
          current_signed_in_organization_id: currentOrganizationId,
          referral_sms_template: referralSmsTemplate,
          referral_call_templates: referralCallTemplates,
          referral_reply_templates: referralReplyTemplates,
        },
      } = response;
      const normalizedDocumentCategories = normalizeDocumentCategories(documentCategories);

      if (response.status === 201) {
        // Canary routing: if the user's org maps to a different deployment than
        // the page was served from, hard-navigate before any redux/localStorage
        // work — the destination will hydrate from a fresh /api/v1/users fetch.
        if (redirectIfDeploymentMismatch(response)) return;
        // remove the auth token from localStorage since the user will not need
        // to click resend code.
        localStorage.removeItem('otp_auth_token');
        clearOtpDelivery();
        localStorage.setItem('m8_has_completed_walkthrough', hasCompletedWalkthrough);
        Sentry.setUser({
          email: data.email,
        });
        reportCdnReachabilityAfterAuth(response?.data?.routing_org_id);
        reportAssetRescueActivationAfterAuth(response?.data?.routing_org_id);

        // Fetches feature flag data
        const featureResponse = await fetchFeatureFlags().catch((error) => {
          return handleUnexpectedAuthError(error, dispatch, USER_AUTH.LOGIN_FAILURE);
        });

        const { data: { flags, user_flags: userFlags, departments: allDepartments, has_emr_integration_credential: hasEmrIntegrationCredential } } = featureResponse;

        saveFeatureFlags(flags);
        saveUserFlags(userFlags);
        // Update the CSRF token to match the authenticated session cookie
        const newCSRFToken = response.headers['x-csrf-token'];
        document.getElementsByName('csrf-token')[0].setAttribute('content', newCSRFToken);
        localStorage.setItem('m8_billing_status', JSON.stringify(response.data.billing_status));

        // Sets the new access token, expiry and client token onto the localStorage.
        // Sets value to single space since ther is no expiry in the response.
        localStorage.setItem('m8_access', access_token);
        localStorage.setItem('m8_client', client);
        localStorage.setItem('m8_expiry', ' ');
        localStorage.setItem('m8_chrome_ext_access', chrome_access_token);
        localStorage.setItem('m8_chrome_ext_client', chrome_client);
        localStorage.setItem('m8_chrome_ext_expiry', ' ');
        if (response.data.job_title) localStorage.setItem('m8_job_title', response.data.job_title)
        localStorage.setItem('m8_emr', response.data.emr);
        localStorage.setItem('m8_nymbl_tenant_id', response.data.available_emrs.find(emr => emr?.tenant_id)?.tenant_id);
        localStorage.setItem('m8_referral_management', response.data.referral_management);
        localStorage.setItem('m8_manual_ai_on_uploads', response.data.manual_ai_on_uploads || false);
        localStorage.setItem('m8_view_referral_analytics', response.data.view_referral_analytics);
        // Set inbox access flags BEFORE dispatch to avoid race condition where
        // components read localStorage before values are set
        localStorage.setItem('m8_access_received_inbox', response.data.access_received_inbox || false);
        localStorage.setItem('m8_access_sent_inbox', response.data.access_sent_inbox || false);
        localStorage.setItem('m8_access_voice_inbox', response.data.access_voice_inbox || false);
        localStorage.setItem('m8_sent_inbox_own_records_only', response.data.sent_inbox_own_records_only || false);
        localStorage.setItem('m8_patient_recapture', response.data.patient_recapture || false);
        localStorage.setItem('m8_patient_engagement', response.data.patient_engagement || false);
        storeNavPreferences(response.data.nav_preferences, response.data.nav_preferences_organization_id ?? null);

        dispatch({
          type: USER_AUTH.LOGGED_IN,
          payload: {
            name,
            uid: email,
            trialUser: trial_user,
            departments,
            voiceInboxDepartments,
            flags,
            allDepartments,
            billing: billingStatus,
            emr: response.data.emr || '',
            jobTitle: response.data.job_title || '',
            defaultFaxNumber: response.data.user_fax_number,
            associatedFaxNumbers: response.data.associated_fax_numbers,
            isAdmin: response.data['is_user_admin?'],
            navPreferences: normalizeNavPreferences(response.data.nav_preferences),
            navPreferencesOrganizationId: response.data.nav_preferences_organization_id ?? null,
            organization_name: response.data.organization_name,
            autoUploadEmr: response.data.auto_upload_emr,
            documentCategories: normalizedDocumentCategories,
            referralManagement: response.data.referral_management,
            manualAiOnUploads: response.data.manual_ai_on_uploads,
            viewReferralAnalytics: response.data.view_referral_analytics,
            accessVoiceInbox: response.data.access_voice_inbox || false,
            patientRecapture: response.data.patient_recapture || false,
            patientEngagement: response.data.patient_engagement || false,
            hasCompletedWalkthrough,
            userCanSendFaxRecordsOnly,
            organizationDetails,
            intercomHmac,
            currentOrganizationId,
            impersonating: false,
            hasEmrIntegrationCredential,
          },
        });

        await showMultiOrganizationLoginModal(dispatch);

        // Save fax numbers data
        const { user_fax_number: userFaxNumber, associated_fax_numbers: associatedFaxNumbers } = response.data;
        const defaultFaxNumber = response.data.user_fax_number !== null ? userFaxNumber : '';
        localStorage.setItem('default_fax_number', defaultFaxNumber);
        localStorage.setItem('associated_fax_numbers', JSON.stringify(associatedFaxNumbers));
        localStorage.setItem('user_can_send_fax_records_only', JSON.stringify(userCanSendFaxRecordsOnly));
        localStorage.setItem('m8_plan_type', billingStatus.plan_type);
        localStorage.setItem('allDepartments', JSON.stringify(allDepartments) || []);
        localStorage.setItem('phone_number', response.data.phone_number);
      } else if (response.status === 200) {
        // store variables in localStorage to be accesed by the Login component
        localStorage.setItem('needs2FA', true);
        localStorage.setItem('email', data.email);
        localStorage.setItem('otp_auth_token', response.data.otp_auth_token);
        localStorage.setItem('phone_number', response.data.phone_number);
        // Older backends omit both fields: no stored delivery keeps the existing
        // email/SMS copy on the OTP screen.
        saveOtpDelivery({
          otpDelivery: response.data.otp_delivery,
          authenticatorAppEnabled: response.data.authenticator_app_enabled,
        });
        // call the RESEND_OTP.REDIRECT action which will redirect to login page.
        dispatch({
          type: RESEND_OTP.REDIRECT,
        });
      }
      // Store the user email and name in localStorage
      localStorage.setItem('m8_uid', email);
      localStorage.setItem('m8_name', name);
      localStorage.setItem('trial_user', trial_user);
      localStorage.setItem('m8_org', organizationName);
      localStorage.setItem('m8_impersonating', false);
      localStorage.setItem('m8_auto_upload_emr', autoUploadEmr);
      localStorage.setItem('m8_document_categories', serializeStorageValue(normalizedDocumentCategories, '[]'));
      localStorage.setItem('is_admin', response.data['is_user_admin?']);
      localStorage.setItem('departments', JSON.stringify(departments));
      localStorage.setItem('voice_inbox_departments', JSON.stringify(voiceInboxDepartments || []));
      localStorage.setItem('login_provider', provider);
      localStorage.setItem('customer_payment_id', customerPaymentId);
      localStorage.setItem('tracking', tracking);
      localStorage.setItem('referral_sms_template', serializeStorageValue(referralSmsTemplate, '[]'));
      localStorage.setItem('referral_call_templates', serializeStorageValue(referralCallTemplates, '[]'));
      localStorage.setItem('referral_reply_templates', serializeStorageValue(referralReplyTemplates, '[]'));
    }).catch((error) => {
      const forceRefresh = error.response.data.force || false;
      const alreadySignedIn = error.response && error.response.status === 409;
      const errorMessage = handleError(error);
      // Remove from localStorage if an OTP error is encountered, keep the
      // auth token in case the user clicks resend code
      if (errorMessage === 'The verification code was incorrect or expired, please try again.') {
        localStorage.setItem('needs2FA', true);
        localStorage.setItem('email', data.email);
      }
      dispatch({
        type: USER_AUTH.LOGIN_FAILURE,
        payload: {
          error: errorMessage,
        },
      });
      // If the API asks us to force refresh the page in the event of a CSRF
      // or cookie mismatch, wait 2 seconds before initiating
      if (forceRefresh || alreadySignedIn) {
        setTimeout(() => {
          window.location.reload();
        }, 200);
      }
    });
  } catch (error) {
    handleUnexpectedAuthError(error, dispatch, USER_AUTH.LOGIN_FAILURE);
  }
};

// Action dispatched when the Resend Security code link is clicked
const resendOtp = data => async (dispatch) => {
  localStorage.setItem('needs2FA', true);
  // To show the loader after making the resendOtp request
  dispatch({
    type: RESEND_OTP.RESEND_OTP_START,
    payload: {
      sentViaEmail: !data.send_otp_via_sms,
    },
  });
  // Make a call to the resend code endpoint
  try {
    axios({
      method: 'POST',
      url: '/auth/resend_token',
      data: {
        user: data,
      },
      headers: {
        'X-CSRF-Token': document.getElementsByName('csrf-token')[0].content,
      },
    }).then((response) => {
      // If a response OK is returned, show a toastr message, store variables to localStorage
      // and dispatch the action
      /* global toastr */
      toastr.success(response.data.info);
      localStorage.setItem('needs2FA', true);
      localStorage.setItem('email', data.email);
      localStorage.setItem('otp_auth_token', response.data.otp_auth_token);
      saveResentOtpDelivery(data.send_otp_via_sms ? OTP_DELIVERY.sms : OTP_DELIVERY.email);

      dispatch({
        type: RESEND_OTP.RESEND_OTP_FULFILLED,
        payload: {
          otp_auth_token: response.data.otp_auth_token,
          email: data.email,
          sentViaEmail: !data.send_otp_via_sms,
        },
      });
      // Update the CSRF token to match the authenticated session cookie
      const newCSRFToken = response.headers['x-csrf-token'];
      document.getElementsByName('csrf-token')[0].setAttribute('content', newCSRFToken);
      // Dispatch this action if theres an error returned from the backend
    }).catch((error) => {
      // The login page remounts on the OTP screen (needs2FA was set above) and
      // needs the address again to submit a code or retry the resend.
      if (data.email) localStorage.setItem('email', data.email);
      dispatch({
        type: RESEND_OTP.RESEND_OTP_FAILED,
        payload: {
          error: error.response.data.error,
          sentViaEmail: !data.send_otp_via_sms,
        },
      });
    });
  } catch (error) {
    handleUnexpectedAuthError(error, dispatch, USER_AUTH.LOGIN_FAILURE);
  }
};

// Action dispatched when the Start Over link is clicked
const redirectToLogin = () => async (dispatch) => {
  clearOtpDelivery();
  dispatch({
    type: RESEND_OTP.REDIRECT,
  });
};

const accessCodeAuth = (payload, gcaptcha, checked) => async (dispatch) => {
  const { 
    receiver_fax: faxCode,
    access_code: accessCode,
  } = payload;
  dispatch({
    type: ACCESS_CODE_AUTH.BEGIN,
  });
  try {
    const response = await axios({
      method: 'POST',
      url: '/api/v1/fax',
      headers: {
        'X-CAPTCHA': gcaptcha,
        'X-CSRF-Token': document.getElementsByName('csrf-token')[0].content,
      },
      data: { access_fax: payload },
    });
    Sentry.setUser({
      fax: faxCode,
    });
    const { 
      send_token: sendToken,
      has_reply: hasResponse,
      subject,
      source,
      recipient_name: recipientName,
      signature_required: signatureRequired,
      should_flatten: shouldFlatten,
      nutrient_viewer: nutrientViewer,
    } = response.data;
    dispatch({
      type: ACCESS_CODE_AUTH.SUCCESS,
      payload: {
        url: response.data.signed_url,
        fax: {
          fax_code: faxCode,
          access_code: accessCode,
          source,
        },
        shouldFlatten,
        nutrientViewer,
        sendToken,
        hasResponse,
        subject,
        recipientName,
        signatureRequired,
      },
    });
    // Remember the fax number code for future sign ins
    if (checked === true) {
      localStorage.setItem('m8_fax', faxCode);
    } else {
      localStorage.removeItem('m8_fax');
    }

    // Store guest token in localStorage
    localStorage.setItem('m8_guest_token', response.data.guest_token);

    // Remove invalid login flag to allow user to login without captcha.
    localStorage.removeItem(faxCode);

    dispatch(push('/app/access'));
  } catch (error) {
    // Add fax number to localStorage to force user to login with captcha.
    localStorage.setItem(faxCode, 1);

    // Customize error message when record is revoked
    let errorMessage;
    if (error.response.status === 403) {
      errorMessage = error.response.data.message || error.response.data.error;
    } else {
      errorMessage = 'Invalid access or fax code entered. The code is case sensitive.';
    }

    dispatch({
      type: ACCESS_CODE_AUTH.FAILURE,
      payload: {
        error: errorMessage,
      },
    });
  }
};

const logoutGuest = () => async (dispatch) => {
  dispatch({
    type: ACCESS_CODE_LOGOUT.LOGOUT_SUCCESS,
  });
  dispatch(push('/app/access_code'));
};

const verifyGuestToken = () => async (dispatch) => {
  dispatch({
    type: VERIFY_GUEST_TOKEN.BEGIN,
  });
  axios({
    method: 'POST',
    url: '/api/v1/fax/verify_guest',
    headers: {
      'X-CSRF-Token': document.getElementsByName('csrf-token')[0].content,
    },
    data: {
      guest_token: localStorage.getItem('m8_guest_token'),
    },
  }).then((response) => {
    dispatch({
      type: VERIFY_GUEST_TOKEN.SUCCESS,
      payload: {
        receiver_name: response.status === 200 ? response.data.receiver_name : '',
      },
    });
  }).catch((error) => {
    localStorage.removeItem('m8_guest_token');
    if (error.response && error.response.status === 400) {
      dispatch({
        type: VERIFY_GUEST_TOKEN.FAILURE,
        payload: {
          error: error.response.data.error,
        },
      });
    } else {
      dispatch({
        type: VERIFY_GUEST_TOKEN.FAILURE,
        payload: {
          error: 'There was an issue processing your request on our end. Please try again later.',
        },
      });
    }
  });
};

const redirectToCreateGuestAccount = () => async (dispatch) => {
  dispatch(push('/app/access/sign_up'));
};

const createGuestAccount = (name, email, password, passwordConfirmation) => async (dispatch) => {
  dispatch({
    type: CREATE_GUEST_ACCOUNT.BEGIN,
  });
  axios({
    method: 'POST',
    url: '/auth/sign_up/guest',
    data: {
      user: {
        name,
        email,
        password,
        password_confirmation: passwordConfirmation,
      },
      guest_token: localStorage.getItem('m8_guest_token'),
    },
    headers: {
      'X-CSRF-Token': document.getElementsByName('csrf-token')[0].content,
    },
  }).then((response) => {
    toastr.success('Successfully created your account!');
    localStorage.removeItem('m8_guest_token');
    localStorage.setItem('otp_auth_token', response.data.otp_auth_token);
    localStorage.setItem('email', email);
    localStorage.setItem('needs2FA', true);
    // The new account's code goes by email; drop any delivery left by an
    // earlier sign-in on this browser.
    clearOtpDelivery();

    // call the RESEND_OTP.REDIRECT action which will reset the global state.
    dispatch({
      type: RESEND_OTP.REDIRECT,
    });

    // Redirect to login page.
    dispatch(push('/login'));
  }).catch((error) => {
    if (error.response && error.response.status === 400) {
      // If we got back a 400 response, that means there was an issue with the body. Stay on page,
      // and display the error.
      dispatch({
        type: CREATE_GUEST_ACCOUNT.FAILURE,
        payload: {
          error: error.response.data.error,
        },
      });
    } else if (error.response && error.response.status === 401) {
      localStorage.removeItem('m8_guest_token');
      dispatch({
        type: VERIFY_GUEST_TOKEN.FAILURE,
        payload: {
          error: 'Your session has expired, please login again to create your account.',
        },
      });
    } else {
      // Send error to sentry.
      captureNetworkException(error, { source: 'createGuestAccount', endpoint: '/auth/sign_up/guest', method: 'POST' });
      localStorage.removeItem('m8_guest_token');
      dispatch({
        type: VERIFY_GUEST_TOKEN.FAILURE,
        payload: {
          error: 'There was an issue processing your request on our end. Please try again later.',
        },
      });
    }
  });
};

const patientLogin = data => async (dispatch) => {
  // The patient portal must be accessed using a uniquely generated link for
  // each record. If the right parameters are not in the URL, we abort the
  // request
  if (!data.token || !data.file_token) {
    return dispatch({
      type: PATIENT_AUTH.PATIENT_LOGIN_FAILURE,
      payload: {
        error: 'Your credentials were not accepted. Please access the original link sent to you and try again.',
      },
    });
  }
  dispatch({
    type: PATIENT_AUTH.PATIENT_LOGGING_IN,
  });
  try {
    axios({
      method: 'POST',
      url: '/api/v1/patients/sign_in',
      data: {
        patient: data,
      },
      headers: {
        'X-CSRF-Token': document.getElementsByName('csrf-token')[0].content,
      },
    }).then((response) => {
      // store variables in localStorage to be accessed by the Login component
      localStorage.setItem('m8_needs_security', true);
      if (response.data.security_question) {
        // Store question in localStorage.
        localStorage.setItem('m8_security_question', response.data.security_question);
      }
      localStorage.setItem('m8_patient_recipient', data.recipient);
      localStorage.setItem('m8_file_token', data.file_token);
      localStorage.setItem('m8_patient_token', data.token);
      localStorage.setItem('m8_auth_token', response.data.auth_token);
      dispatch({
        type: SECURITY_QUESTION.PATIENT_REDIRECT,
      });
    }).catch((error) => {
      const errorMessage = handleError(error);
      dispatch({
        type: PATIENT_AUTH.PATIENT_LOGIN_FAILURE,
        payload: {
          error: errorMessage,
        },
      });
    });
  } catch (error) {
    handleUnexpectedAuthError(error, dispatch, PATIENT_AUTH.PATIENT_LOGIN_FAILURE);
  }
};

const patientSecurityLogin = data => async (dispatch) => {
  dispatch({
    type: PATIENT_AUTH.PATIENT_LOGGING_IN,
  });
  try {
    axios({
      method: 'POST',
      url: '/api/v1/patients/validate',
      data: {
        patient: data,
      },
      headers: {
        'X-CSRF-Token': document.getElementsByName('csrf-token')[0].content,
      },
    }).then((response) => {
      if (response.status === 200) {
        // store variables in localStorage to be accesed by the Login component
        localStorage.setItem('m8_needs_security', true);
        if (response.data.security_question) {
          // Store question in localStorage.
          localStorage.setItem('m8_security_question', response.data.security_question);
        }
        localStorage.setItem('m8_patient_recipient', data.recipient);
        localStorage.setItem('m8_auth_token', response.data.auth_token);
        dispatch({
          type: PATIENT_AUTH.PATIENT_LOGGED_IN,
          payload: {
            url: response.data.signed_url,
            auth_token: data.auth_token,
            security_token: data.file_token,
            patientToken: data.patient_token,
            recipient: data.recipient,
            sendToken: response.data.send_token,
            recipientName: response.data.recipient_name,
            recipientEmail: response.data.recipient,
            hasResponse: response.data.has_reply,
            signatureRequired: response.data.signature_required,
            disableSecureReplies: response.data.disable_replies,
            should_flatten: response.data.should_flatten,
            nutrientViewer: response.data.nutrient_viewer,
            nameToSignatureRequired: response.data.name_to_signature_required,
          },
        });
        Sentry.setUser({
          patient: data.email,
        });

        dispatch(push('/app/patient_access'));
      }
    }).catch((error) => {
      const errorMessage = handleError(error);
      // Remove from localStorage if Security Question error is encountered, keep the
      // auth token in case the user clicks resend code
      if (errorMessage === 'Your answer was incorrect. Please try again or contact Medsender support.') {
        localStorage.setItem('m8_needs_security', true);
        localStorage.setItem('m8_security_question', data.question);
        localStorage.setItem('m8_patient_recipient', data.recipient);
        localStorage.setItem('m8_auth_token', data.auth_token);
        localStorage.setItem('m8_file_token', data.file_token);
        localStorage.setItem('m8_patient_token', data.patient_token);
      }
      dispatch({
        type: PATIENT_AUTH.PATIENT_LOGIN_FAILURE,
        payload: {
          error: errorMessage,
        },
      });
    });
  } catch (error) {
    handleUnexpectedAuthError(error, dispatch, PATIENT_AUTH.PATIENT_LOGIN_FAILURE);
  }
};

/**
 * Makes request to set the patient's security question and answer. Will log the patient in and
 * redirect to record viewer on success.
 */
const patientSetQuestionAndLogin = data => async (dispatch) => {
  dispatch({
    type: PATIENT_AUTH.PATIENT_LOGGING_IN,
  });
  try {
    axios({
      method: 'PUT',
      url: '/api/v1/patients',
      data: {
        patient: data,
      },
      headers: {
        'X-CSRF-Token': document.getElementsByName('csrf-token')[0].content,
      },
    }).then((response) => {
      if (response.status === 200) {
        // store variables in localStorage to be accessed by the Login component
        localStorage.setItem('m8_needs_security', true);
        localStorage.setItem('m8_patient_recipient', data.recipient);
        localStorage.setItem('m8_auth_token', response.data.auth_token);

        dispatch({
          type: PATIENT_AUTH.PATIENT_LOGGED_IN,
          payload: {
            url: response.data.signed_url,
            auth_token: data.auth_token,
            security_token: data.file_token,
            patientToken: data.patient_token,
            email: data.email,
            sendToken: response.data.send_token,
            recipientName: response.data.recipient_name,
            recipient: data.recipient,
            recipientEmail: response.data.recipient,
            hasResponse: response.data.has_reply,
            signatureRequired: response.data.signature_required,
            disableSecureReplies: response.data.disable_replies,
            should_flatten: response.data.should_flatten,
            nutrientViewer: response.data.nutrient_viewer,
            nameToSignatureRequired: response.data.name_to_signature_required,
          },
        });

        dispatch(push('/app/patient_access'));
      }
    }).catch((error) => {
      const errorMessage = handleError(error);
      dispatch({
        type: PATIENT_AUTH.PATIENT_LOGIN_FAILURE,
        payload: {
          error: errorMessage,
        },
      });
    });
  } catch (error) {
    handleUnexpectedAuthError(error, dispatch, PATIENT_AUTH.PATIENT_LOGIN_FAILURE);
  }
};

// TODO (Ammar Khan)
// Align these routes with backend properly
const changePassword = data => async (dispatch) => {
  try {
    await axios({
      method: 'PATCH',
      url: '/auth/password',
      data: {
        user: data,
      },
      headers: {
        'X-CSRF-Token': document.getElementsByName('csrf-token')[0].content,
      },
    });
    dispatch({
      type: USER_PROFILE.CHANGE_PASSWORD_SUCCESS,
      payload: {
        message: 'Password changed successfully',
        success: true,
      },
    });
  } catch (error) {
    dispatch({
      type: USER_PROFILE.CHANGE_PASSWORD_FAILED,
      payload: {
        message: error.response.data.error,
        success: false,
      },
    });
  }
};

/** Checks if the reset password token is valid
 * @param {Object} - data - data object sent as params
 * @param {string} - data.reset_password_token
 */
const validateResetToken = (data, passwordSet) => async (dispatch) => {
  dispatch({
    type: VALIDATE_RESET_TOKEN.BEGIN,
  });

  try {
    const response = await axios({
      method: 'POST',
      url: '/auth/password/validate',
      data: {
        password: data,
      },
      headers: {
        'X-CSRF-Token': document.getElementsByName('csrf-token')[0].content,
      },
    });
    dispatch({
      type: VALIDATE_RESET_TOKEN.SUCCESS,
      payload: {
        message: response.message,
        resetPasswordEmail: response.data.email,
        resetPasswordToken: data.reset_password_token,
        passwordSet,
      },
    });
  } catch (error) {
    let errorMessage = 'Reset password request is invalid or has expired.\n' +
     'Please contact your administrator.';
    if (error.response.status === 400) {
      errorMessage = error.response.data.error;
    }

    dispatch({
      type: VALIDATE_RESET_TOKEN.FAILED,
      payload: {
        message: errorMessage,
      },
    });
    // Displays error message
    toastr.error(errorMessage);
  }
};

const forgotPassword = email => async (dispatch) => {
  dispatch({ type: FORGOT_PASSWORD.BEGIN });
  try {
    const response = await axios({
      method: 'POST',
      url: '/auth/password/reset_password',
      data: {
        email,
      },
      headers: {
        'X-CSRF-Token': document.getElementsByName('csrf-token')[0].content,
      },
    });
    dispatch({
      type: FORGOT_PASSWORD.SUCCESS,
      payload: response.data,
    });
  } catch (e) {
    dispatch({
      type: FORGOT_PASSWORD.FAILED,
      payload: e.response,
    });
  }
};

/** Reset password and updates the store
 * @param {Object} - data - data object sent as params
 * @param {string} - data.password
 * @param {string} - data.confirm_password
 * @param {string} - data.reset_password_token
 */
const resetPassword = data => async (dispatch) => {
  dispatch({
    type: RESET_PASSWORD.BEGIN,
  });
  try {
    if (data.password === '' || data.password_confirmation === '') {
      throw new Error('Password or the confirmation field cannot be blank.');
    }

    const response = await axios({
      method: 'PUT',
      url: '/auth/password/reset_password',
      data: {
        password: data,
      },
      headers: {
        'X-CSRF-Token': document.getElementsByName('csrf-token')[0].content,
      },
    });

    // Canary routing: reset-password is a sign-in path — if the user's org
    // maps to a different deployment than the page was served from, hard-
    // navigate before any redux/localStorage writes so the destination
    // hydrates from a fresh /api/v1/users fetch on the correct bundle.
    if (redirectIfDeploymentMismatch(response)) return;

    const successMessage = 'Password has been changed successfully.';

    // Fetch feature flags to get hasEmrIntegrationCredential
    const featureResponse = await fetchFeatureFlags().catch((error) => {
      return handleUnexpectedAuthError(error, dispatch, RESET_PASSWORD.FAILED);
    });

    const { data: { flags, user_flags: userFlags, departments: allDepartments, has_emr_integration_credential: hasEmrIntegrationCredential } } = featureResponse;
    const normalizedDocumentCategories = normalizeDocumentCategories(response.data.document_categories);

    dispatch({
      type: RESET_PASSWORD.SUCCESS,
      payload: {
        message: successMessage,
        name: response.data.name,
        uid: response.data.email,
        trialUser: response.data.trial_user,
        organization: response.data.organization_name,
        autoUploadEmr: response.data.auto_upload_emr,
        documentCategories: normalizedDocumentCategories,
        emr: response.data.emr,
        referralManagement: response.data.referral_management,
        manualAiOnUploads: response.data.manual_ai_on_uploads,
        viewReferralAnalytics: response.data.view_referral_analytics,
        accessVoiceInbox: response.data.access_voice_inbox || false,
        patientRecapture: response.data.patient_recapture || false,
        patientEngagement: response.data.patient_engagement || false,
        billing: response.data.billing_status,
        jobTitle: response.data.job_title || '',
        defaultFaxNumber: response.data.user_fax_number || '',
        associatedFaxNumbers: response.data.associated_fax_numbers,
        departments: response.data.organization_departments,
        hasCompletedWalkthrough: response.data.has_completed_walkthrough,
        isAdmin: response.data['is_user_admin?'],
        navPreferences: normalizeNavPreferences(response.data.nav_preferences),
        navPreferencesOrganizationId: response.data.nav_preferences_organization_id ?? null,
        organizationDetails: response.data.organization_details,
        availableEmrs: response.data.available_emrs,
        currentOrganizationId: response.data.current_signed_in_organization_id,
        hasEmrIntegrationCredential,
      },
    });

    saveFeatureFlags(flags, allDepartments);
    saveUserFlags(userFlags);
    await showMultiOrganizationLoginModal(dispatch);

    // Display toastr if password reset is successful
    toastr.success(successMessage);
    const { user_fax_number: userFaxNumber, associated_fax_numbers: associatedFaxNumbers } = response.data;
    const defaultFaxNumber = response.data.user_fax_number !== null ? userFaxNumber : '';

    // Store auth tokens on localStorage
    localStorage.setItem('m8_billing_status', JSON.stringify(response.data.billing_status));
    localStorage.setItem('m8_access', response.data.access_token);
    localStorage.setItem('m8_client', response.data.client);
    localStorage.setItem('m8_chrome_ext_access', response.data.chrome_access_token);
    localStorage.setItem('m8_chrome_ext_client', response.data.chrome_client);
    localStorage.setItem('m8_name', response.data.name);
    localStorage.setItem('m8_org', response.data.organization_name);
    localStorage.setItem('m8_auto_upload_emr', response.data.auto_upload_emr);
    localStorage.setItem('m8_document_categories', serializeStorageValue(normalizedDocumentCategories, '[]'));
    localStorage.setItem('trial_user', response.data.trial_user);
    localStorage.setItem('m8_job_title', response.data.job_title);
    localStorage.setItem('m8_emr', response.data.emr);
    localStorage.setItem('m8_nymbl_tenant_id', response.data.available_emrs?.find((emr) => emr?.tenant_id)?.tenant_id);
    localStorage.setItem('m8_referral_management', response.data.referral_management);
    localStorage.setItem('m8_manual_ai_on_uploads', response.data.manual_ai_on_uploads || false);
    localStorage.setItem('m8_view_referral_analytics', response.data.view_referral_analytics || false);
    localStorage.setItem('m8_access_voice_inbox', response.data.access_voice_inbox || false);
    localStorage.setItem('m8_sent_inbox_own_records_only', response.data.sent_inbox_own_records_only || false);
    localStorage.setItem('m8_patient_recapture', response.data.patient_recapture || false);
    localStorage.setItem('m8_patient_engagement', response.data.patient_engagement || false);
    storeNavPreferences(response.data.nav_preferences, response.data.nav_preferences_organization_id ?? null);
    localStorage.setItem('default_fax_number', defaultFaxNumber);
    localStorage.setItem('associated_fax_numbers', JSON.stringify(associatedFaxNumbers));
    localStorage.setItem('m8_uid', response.data.email);
    localStorage.setItem('is_admin', response.data['is_user_admin?']);
    localStorage.setItem('departments', JSON.stringify(response.data.organization_departments));
    localStorage.setItem('voice_inbox_departments', JSON.stringify(response.data.voice_inbox_departments || []));
    localStorage.setItem('m8_has_completed_walkthrough', JSON.stringify(response.data.has_completed_walkthrough));
    localStorage.setItem('m8_plan_type', response.data.billing_status.plan_type);
  } catch (error) {
    const errorMessage = error.response ? error.response.data.error : error.message;
    dispatch({
      type: RESET_PASSWORD.FAILED,
      payload: {
        message: errorMessage,
        resetPasswordToken: data.reset_password_token,
      },
    });
  }
};

/**
 * Prompts user with dialog to sign out if they're trying to access patient portal while signed in
 * to a Medsender account.
 */
const resetPatientAccessAttempt = () => async (dispatch) => {
  dispatch({ type: PATIENT_AUTH.RESET_PATIENT_ACCESS_ATTEMPT });
};

/**
 * Logs in the user to Medsender after login via the modal.
 * @param {Object} user - The user who is authenticated.
 * @param {string} user.name - The name of the user.
 * @param {string} user.uid - The initials of the user's name.
 */
const switchAuth = (user, dispatch) => {
  dispatch({
    type: 'SWITCH_AUTH',
    payload: {
      user,
    },
  });
};

/**
 * Mints the one-time sign-in token the printer sign-in page hands to Gibraltar or the Medsender
 * iOS app.
 * @param  {Object} options
 * @param  {String} options.client 'ios' when the page was opened by the Medsender iOS app: then a
 *   portal-only organization may sign in too (Api::V1::PrinterTokensController). Gibraltar supplies an S256 challenge and state.
 */
const printerSignIn = ({
  client, code_challenge: codeChallenge, code_challenge_method: codeChallengeMethod, state,
} = {}) => {
  const data = client ? { client } : null;
  if (client === 'gibraltar') {
    Object.assign(data, { code_challenge: codeChallenge, code_challenge_method: codeChallengeMethod, state });
  }
  return crud({
    dispatch: {
      begin: 'BEGIN_PRINTER_SIGN_IN',
      fail: 'FAILED_PRINTER_SIGN_IN',
      end: 'FINISHED_PRINTER_SIGN_IN',
    },
    method: 'POST',
    url: '/api/v1/printer_tokens',
    data,
  });
};

/**
 * Disable replying on the modal after a reply has been made on the viewer.
 */
const disableReplying = () => {
  return ({
    type: 'DISABLE_REPLYING',
    payload: {},
  });
};

/**
 * Updates the global store with the newly created fax number.
 */
const updateAssociatedFaxNumber = faxNumber => async (dispatch) => {
  dispatch({
    type: 'UPDATE_ASSOCIATED_FAX_NUMBERS',
    payload: faxNumber,
  });
};

/**
 * Update walkthorugh to the final step after getting fax number.
 */
const updateWalkthroughStep = stepOnWalkthrough => (dispatch) => {
  dispatch({
    type: 'UPDATE_STEP_ON_WALKTHROUGH',
    payload: {
      stepOnWalkthrough,
    },
  });
};

// Run AI reads state.auth.manualAiOnUploads, otherwise refreshed only at sign-in.
// Only for an admin who just turned the org feature on or off: admins always
// pass User#manual_ai_on_uploads while the org has it, so no round trip is
// needed (ENG-3664). The localStorage write survives reloads, which rehydrate
// from that key.
const setManualAiOnUploads = (enabled) => (dispatch) => {
  try {
    localStorage.setItem('m8_manual_ai_on_uploads', enabled);
  } catch {
    // Redux still updates the button for this page load.
  }
  dispatch({ type: USER_AUTH.MANUAL_AI_ON_UPLOADS_CHANGED, payload: { enabled } });
};

export {
  login,
  logout,
  resetAuth,
  validateAuth,
  extendUserSession,
  accessCodeAuth,
  logoutGuest,
  verifyGuestToken,
  redirectToCreateGuestAccount,
  createGuestAccount,
  patientLogin,
  patientSecurityLogin,
  patientSetQuestionAndLogin,
  changePassword,
  validateResetToken,
  resetPassword,
  forgotPassword,
  resendOtp,
  redirectToLogin,
  resetPatientAccessAttempt,
  switchAuth,
  disableReplying,
  updateAssociatedFaxNumber,
  printerSignIn,
  getLoginInfo,
  updateWalkthroughStep,
  showMultiOrganizationLoginModal,
  setManualAiOnUploads,
};
